import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { pageTransition, formatDate, fullName } from "@/lib/utils";
import {
  getEmployee,
  getOnboardingReadiness,
  startOnboarding,
  updateEmployee,
} from "@/api/employees";
import type { OnboardingReadiness } from "@/types/employee";
import { useToast } from "@/hooks/useToast";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Avatar } from "@/components/ui/Avatar";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Tabs } from "@/components/ui/Tabs";
import { SkeletonCard } from "@/components/ui/Skeleton";
import { Modal } from "@/components/ui/Modal";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import {
  AlertTriangle,
  ArrowLeft,
  FileText,
  Mail,
  Phone,
  MapPin,
  Building,
  Calendar,
  Edit,
  Rocket,
  Settings as SettingsIcon,
  UserMinus,
  Send,
} from "lucide-react";
import { inviteEmployee, listInvitations, type InvitationRow } from "@/api/invitations";
import {
  STATUS_LABELS,
  EMPLOYMENT_TYPE_LABELS,
} from "@/lib/constants";
import type { Employee } from "@/types/employee";
import { usePermissions } from "@/hooks/usePermissions";
import { useAuth } from "@/hooks/useAuth";
import { EmergencyContactsEditor } from "@/components/employees/EmergencyContactsEditor";
import { CompensationHistoryPanel } from "@/components/employees/CompensationHistoryPanel";
import { CustomFieldsPanel } from "@/components/employees/CustomFieldsPanel";
import { OffboardModal } from "@/components/employees/OffboardModal";
import { EmployeeBenefitsTab } from "@/components/employees/EmployeeBenefitsTab";

export function EmployeeProfilePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { can, hasRole } = usePermissions();
  const { user } = useAuth();

  const [employee, setEmployee] = useState<Employee | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [offboardOpen, setOffboardOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [invitation, setInvitation] = useState<InvitationRow | null>(null);
  const [inviting, setInviting] = useState(false);
  // Onboarding lifecycle controls — confirmation modal + in-flight flag
  // for the Start Onboarding button. Populated count comes from the
  // EmployeeCreatePage preview pattern, so the modal copy can read
  // "X documents…" honestly without an extra API call.
  const [confirmOnboardOpen, setConfirmOnboardOpen] = useState(false);
  const [startingOnboarding, setStartingOnboarding] = useState(false);
  // Readiness modal state machine — exactly one of these is non-null
  // at any time. Drives which modal renders. ``readiness`` carries the
  // backend payload through to the actual confirm step so we don't
  // re-fetch on the modal close.
  const [readiness, setReadiness] = useState<OnboardingReadiness | null>(null);
  const [readinessModal, setReadinessModal] = useState<
    "none" | "no_templates" | "no_applicable" | "empty_files" | "confirm"
  >("none");
  const [checkingReadiness, setCheckingReadiness] = useState(false);

  const loadEmployee = () => {
    if (!id) return;
    // Defensive: if someone lands here with the literal path segment
    // "new" — e.g. via an old bookmark or a stale link — redirect to
    // the create page instead of firing a 404-bound GET /employees/new
    // that would surface as "Failed to load employee".
    if (id === "new") {
      navigate("/employees/new", { replace: true });
      return;
    }
    setIsLoading(true);
    getEmployee(id)
      .then(setEmployee)
      .catch(() => addToast("error", "Failed to load employee"))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    loadEmployee();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Invitation state is HR-only; silently ignore 403 for non-admin viewers.
  useEffect(() => {
    if (!id || !can("manage_employees")) return;
    let cancelled = false;
    listInvitations()
      .then((rows) => {
        if (cancelled) return;
        setInvitation(rows.find((r) => r.employee_id === id) ?? null);
      })
      .catch(() => {
        /* ignore */
      });
    return () => {
      cancelled = true;
    };
  }, [id, can]);

  // Pre-flight readiness — ALWAYS runs before any modal opens. The
  // backend gate runs the same checks server-side, so this is purely
  // for UX (don't pop the confirm modal if we already know it'll
  // 409/422). The fall-through to the confirm modal happens when
  // every applicable template has its file in place.
  const handleStartOnboardingClick = async () => {
    if (!employee) return;
    setCheckingReadiness(true);
    try {
      const r = await getOnboardingReadiness(employee.id);
      setReadiness(r);
      if (!r.eligible) {
        if (r.reason === "no_templates") {
          setReadinessModal("no_templates");
          setConfirmOnboardOpen(true);
          return;
        }
        if (r.reason === "no_applicable_templates") {
          setReadinessModal("no_applicable");
          setConfirmOnboardOpen(true);
          return;
        }
        // Other ineligibility (already in_progress / completed /
        // not_applicable) — surface inline; the SPA usually hides the
        // button here but a tab open across a state change can race.
        addToast(
          "error",
          "This employee can't be onboarded right now.",
        );
        return;
      }
      setReadinessModal(
        r.empty_templates.length > 0 ? "empty_files" : "confirm",
      );
      setConfirmOnboardOpen(true);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      addToast(
        "error",
        ax.response?.data?.detail || "Couldn't check onboarding readiness",
      );
    } finally {
      setCheckingReadiness(false);
    }
  };

  const handleConfirmOnboarding = async (force = false) => {
    if (!employee) return;
    setStartingOnboarding(true);
    try {
      const res = await startOnboarding(employee.id, { force });
      // 0-docs warning: onboarding "started" but the new hire has
      // nothing to sign. Surface this loudly so the admin notices —
      // otherwise the employee logs in to an empty My Documents page
      // and assumes the platform is broken.
      if ((res.documents_assigned ?? 0) === 0) {
        addToast(
          "warning",
          "Onboarding started, but no documents were assigned. " +
            "Check that your active templates target this employee's " +
            "department / employment type, or open Settings → Documents " +
            "and turn on \"Applies to all employees\" for at least one " +
            "template.",
        );
      } else {
        addToast(
          "success",
          `Onboarding started — ${res.documents_assigned} document${
            res.documents_assigned === 1 ? "" : "s"
          } assigned`,
        );
      }
      setConfirmOnboardOpen(false);
      setReadinessModal("none");
      setReadiness(null);
      // Re-fetch so the badge + button state reflect the IN_PROGRESS
      // transition without a manual reload.
      loadEmployee();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      addToast(
        "error",
        ax.response?.data?.detail || "Couldn't start onboarding",
      );
    } finally {
      setStartingOnboarding(false);
    }
  };

  const closeOnboardModal = () => {
    if (startingOnboarding) return;
    setConfirmOnboardOpen(false);
    setReadinessModal("none");
    setReadiness(null);
  };

  const handleResendInvite = async () => {
    if (!employee) return;
    setInviting(true);
    try {
      await inviteEmployee(employee.id);
      addToast("success", "Invitation sent");
      const rows = await listInvitations();
      setInvitation(rows.find((r) => r.employee_id === employee.id) ?? null);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      addToast("error", ax.response?.data?.detail || "Failed to send invitation");
    } finally {
      setInviting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="text-center py-16">
        <p className="text-text-muted">Employee not found</p>
        <Button variant="ghost" onClick={() => navigate("/employees")} className="mt-4">
          Back to People
        </Button>
      </div>
    );
  }

  const isSelf = user?.employee_id === employee.id;
  const canEditContacts = can("manage_employees") || isSelf;
  const canManageEmployees = can("manage_employees");
  const canViewCompensation = hasRole("HR_ADMIN", "SUPER_ADMIN") || isSelf;
  const canEditCompensation = hasRole("HR_ADMIN", "SUPER_ADMIN");
  const canOffboard =
    canManageEmployees && employee.status !== "TERMINATED";

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "personal", label: "Personal Info" },
    { id: "employment", label: "Employment" },
    ...(canViewCompensation ? [{ id: "compensation", label: "Compensation" }] : []),
    { id: "additional", label: "Additional Info" },
    { id: "documents", label: "Documents" },
    // Benefits — admin reads/writes per-employee elections here.
    // The COBRA flow at offboard time queries this exact data, so
    // gating it to admins (canManageEmployees) keeps the source-of-
    // truth surface tight.
    ...(canManageEmployees ? [{ id: "benefits", label: "Benefits" }] : []),
  ];

  // Emergency contacts are now fetched by the editor itself via the relational API.

  return (
    <motion.div {...pageTransition}>
      <button
        onClick={() => navigate("/employees")}
        className="flex items-center gap-1 text-sm text-text-muted hover:text-text-primary mb-4 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to People
      </button>

      {/* Profile header */}
      <Card className="mb-6">
        <div className="flex flex-col sm:flex-row items-start gap-6">
          <Avatar
            src={employee.avatar_url}
            firstName={employee.first_name}
            lastName={employee.last_name}
            size="xl"
          />
          <div className="flex-1">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-bold font-heading text-text-primary">
                  {fullName(employee.first_name, employee.last_name, employee.preferred_name)}
                </h2>
                {employee.pronouns && (
                  <span className="text-sm text-text-muted">({employee.pronouns})</span>
                )}
                <p className="text-text-muted mt-0.5">{employee.title}</p>
                <div className="flex items-center gap-3 mt-2 flex-wrap">
                  <Badge
                    variant={
                      employee.status === "ACTIVE" ? "success" : "default"
                    }
                  >
                    {STATUS_LABELS[employee.status]}
                  </Badge>
                  {/* Onboarding state badge — hidden for migrated /
                      existing employees so the profile of someone who's
                      been on the team for years isn't cluttered with a
                      "Not applicable" pill. */}
                  {employee.onboarding_status === "pending" && (
                    <Badge variant="warning">Onboarding Pending</Badge>
                  )}
                  {employee.onboarding_status === "in_progress" && (
                    <Badge variant="info">Onboarding In Progress</Badge>
                  )}
                  {employee.onboarding_status === "completed" && (
                    <Badge variant="success">Onboarding Complete</Badge>
                  )}
                  <span className="text-xs text-text-muted">
                    {EMPLOYMENT_TYPE_LABELS[employee.employment_type]}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {/* Start Onboarding — only when the employee was added
                    as a new hire AND hasn't been kicked off yet. The
                    backend enforces the same precondition; this just
                    keeps the UI tidy. */}
                {canManageEmployees &&
                  employee.onboarding_status === "pending" && (
                    <Button
                      size="sm"
                      onClick={handleStartOnboardingClick}
                      isLoading={checkingReadiness}
                    >
                      <Rocket className="h-4 w-4" />
                      Start Onboarding
                    </Button>
                  )}
                {canManageEmployees &&
                  invitation &&
                  invitation.state !== "active" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleResendInvite}
                      isLoading={inviting}
                      title={
                        invitation.state === "never_invited"
                          ? "Send setup-password invitation"
                          : "Rotates the token and resets the 7-day expiry — any older link will stop working"
                      }
                    >
                      <Send className="h-4 w-4" />
                      {invitation.state === "never_invited"
                        ? "Invite"
                        : "Resend invite"}
                    </Button>
                  )}
                {canOffboard && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setOffboardOpen(true)}
                  >
                    <UserMinus className="h-4 w-4" />
                    Offboard
                  </Button>
                )}
                {canManageEmployees && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setEditOpen(true)}
                  >
                    <Edit className="h-4 w-4" />
                    Edit
                  </Button>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-4 mt-4 text-sm text-text-muted">
              {employee.work_email && (
                <div className="flex items-center gap-1.5">
                  <Mail className="h-4 w-4" />
                  {employee.work_email}
                </div>
              )}
              {employee.phone && (
                <div className="flex items-center gap-1.5">
                  <Phone className="h-4 w-4" />
                  {employee.phone}
                </div>
              )}
              {employee.department && (
                <div className="flex items-center gap-1.5">
                  <Building className="h-4 w-4" />
                  {employee.department.name}
                </div>
              )}
              {employee.office_location && (
                <div className="flex items-center gap-1.5">
                  <MapPin className="h-4 w-4" />
                  {employee.office_location}
                </div>
              )}
              <div className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4" />
                Started {formatDate(employee.hire_date)}
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} className="mb-6" />

      {/* Tab content */}
      {activeTab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Quick Info</CardTitle>
            </CardHeader>
            <dl className="space-y-3">
              <InfoRow label="Department" value={employee.department?.name || "—"} />
              <InfoRow
                label="Manager"
                value={
                  employee.manager
                    ? `${employee.manager.first_name} ${employee.manager.last_name}`
                    : "—"
                }
              />
              <InfoRow label="Employment Type" value={EMPLOYMENT_TYPE_LABELS[employee.employment_type]} />
              <InfoRow label="Work Location" value={employee.work_location || "—"} />
              <InfoRow label="Office" value={employee.office_location || "—"} />
              <InfoRow label="Hire Date" value={formatDate(employee.hire_date)} />
            </dl>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
            </CardHeader>
            <dl className="space-y-3">
              <InfoRow label="Work Email" value={employee.work_email || "—"} />
              <InfoRow label="Personal Email" value={employee.personal_email || "—"} />
              <InfoRow label="Phone" value={employee.phone || "—"} />
            </dl>
          </Card>
        </div>
      )}

      {activeTab === "personal" && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Personal Information</CardTitle>
            </CardHeader>
            <dl className="space-y-3">
              <InfoRow label="Full Name" value={`${employee.first_name} ${employee.last_name}`} />
              <InfoRow label="Preferred Name" value={employee.preferred_name || "—"} />
              <InfoRow label="Pronouns" value={employee.pronouns || "—"} />
              <InfoRow label="Personal Email" value={employee.personal_email || "—"} />
              <InfoRow label="Phone" value={employee.phone || "—"} />
            </dl>
          </Card>

          <EmergencyContactsEditor
            employeeId={employee.id}
            canEdit={canEditContacts}
          />
        </div>
      )}

      {activeTab === "employment" && (
        <Card>
          <CardHeader>
            <CardTitle>Employment Details</CardTitle>
          </CardHeader>
          <dl className="space-y-3">
            <InfoRow label="Title" value={employee.title} />
            <InfoRow label="Department" value={employee.department?.name || "—"} />
            <InfoRow label="Employment Type" value={EMPLOYMENT_TYPE_LABELS[employee.employment_type]} />
            <InfoRow label="Status" value={STATUS_LABELS[employee.status]} />
            <InfoRow label="Hire Date" value={formatDate(employee.hire_date)} />
            {employee.termination_date && (
              <InfoRow label="Termination Date" value={formatDate(employee.termination_date)} />
            )}
          </dl>
        </Card>
      )}

      {activeTab === "compensation" && canViewCompensation && (
        <CompensationHistoryPanel
          employeeId={employee.id}
          canEdit={canEditCompensation}
        />
      )}

      {activeTab === "additional" && (
        <CustomFieldsPanel
          employeeId={employee.id}
          canEditAll={canManageEmployees}
        />
      )}

      {activeTab === "documents" && (
        <Card>
          <div className="text-center py-12 text-text-muted">
            <p>Document management coming in Phase 2</p>
          </div>
        </Card>
      )}

      {activeTab === "benefits" && canManageEmployees && (
        <EmployeeBenefitsTab
          employeeId={employee.id}
          employeeName={`${employee.first_name} ${employee.last_name}`}
        />
      )}

      <OffboardModal
        isOpen={offboardOpen}
        onClose={() => setOffboardOpen(false)}
        employeeId={employee.id}
        employeeName={`${employee.first_name} ${employee.last_name}`}
        onDone={loadEmployee}
      />

      <EditEmployeeModal
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        employee={employee}
        onSaved={() => {
          setEditOpen(false);
          loadEmployee();
        }}
      />

      {/* Start Onboarding pre-flight modal. Four sub-states drive the
          copy + buttons; the Modal itself is reused so the open/close
          lifecycle stays simple. */}
      <Modal
        isOpen={confirmOnboardOpen}
        onClose={closeOnboardModal}
        title={
          readinessModal === "no_templates"
            ? "No documents configured"
            : readinessModal === "no_applicable"
              ? "No matching templates"
              : readinessModal === "empty_files"
                ? "Some templates are missing files"
                : "Start onboarding?"
        }
      >
        {readinessModal === "no_templates" && (
          <>
            <div className="flex items-start gap-3">
              <FileText className="h-5 w-5 text-warning shrink-0 mt-0.5" />
              <div className="text-sm text-text-muted">
                <p className="text-text-primary font-medium">
                  Set up your onboarding documents first.
                </p>
                <p className="mt-1">
                  Before onboarding employees, go to{" "}
                  <strong>Settings → Documents</strong> and configure
                  your company&rsquo;s onboarding documents — NDA,
                  employee handbook, offer letter, direct deposit form,
                  whatever your team uses.
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="secondary" onClick={closeOnboardModal}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  closeOnboardModal();
                  navigate("/settings?tab=doc-config");
                }}
              >
                <SettingsIcon className="h-4 w-4" />
                Go to Document Setup
              </Button>
            </div>
          </>
        )}

        {readinessModal === "no_applicable" && (
          <>
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-warning shrink-0 mt-0.5" />
              <div className="text-sm text-text-muted">
                <p className="text-text-primary font-medium">
                  No active templates target this employee.
                </p>
                <p className="mt-1">
                  Your org has{" "}
                  <strong>
                    {readiness?.total_active_templates ?? 0} active template
                    {readiness?.total_active_templates === 1 ? "" : "s"}
                  </strong>
                  , but none of them apply to this employee&rsquo;s
                  department or employment type. Update the targeting
                  on an existing template, or add one that applies to
                  everyone.
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="secondary" onClick={closeOnboardModal}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  closeOnboardModal();
                  navigate("/settings?tab=doc-config");
                }}
              >
                <SettingsIcon className="h-4 w-4" />
                Open template settings
              </Button>
            </div>
          </>
        )}

        {readinessModal === "empty_files" && readiness && (
          <>
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-warning shrink-0 mt-0.5" />
              <div className="text-sm text-text-muted flex-1">
                <p className="text-text-primary font-medium">
                  These documents are not yet configured:
                </p>
                <ul className="mt-2 space-y-1">
                  {readiness.empty_templates.map((t) => (
                    <li key={t.id} className="flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-warning" />
                      <span className="text-text-primary">{t.name}</span>
                      <span className="text-xs text-text-muted">
                        — no content set up
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="mt-3 text-xs">
                  If you proceed, employees will receive signing
                  requests for empty documents. Configure each
                  template first (upload a PDF, or build a fillable
                  form), or send anyway if you plan to set them up
                  before employees sign.
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2 flex-wrap">
              <Button
                variant="secondary"
                onClick={closeOnboardModal}
                disabled={startingOnboarding}
              >
                Cancel
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  closeOnboardModal();
                  navigate("/settings?tab=doc-config");
                }}
                disabled={startingOnboarding}
              >
                <SettingsIcon className="h-4 w-4" />
                Go to Document Setup
              </Button>
              <Button
                onClick={() => handleConfirmOnboarding(true)}
                isLoading={startingOnboarding}
              >
                <Rocket className="h-4 w-4" />
                Send anyway
              </Button>
            </div>
          </>
        )}

        {readinessModal === "confirm" && readiness && (
          <>
            <p className="text-sm text-text-muted">
              This will assign{" "}
              <strong className="text-text-primary">
                {readiness.applicable_templates.length} document
                {readiness.applicable_templates.length === 1 ? "" : "s"}
              </strong>{" "}
              to{" "}
              <strong className="text-text-primary">
                {employee.first_name} {employee.last_name}
              </strong>
              , notify their manager, and run the Pilot AI welcome flow.
              The employee will receive emails about each document.
            </p>
            {readiness.applicable_templates.length > 0 && (
              <ul className="mt-3 space-y-1 text-xs text-text-muted">
                {readiness.applicable_templates.slice(0, 6).map((t) => (
                  <li key={t.id} className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-success" />
                    <span className="text-text-primary">{t.name}</span>
                  </li>
                ))}
                {readiness.applicable_templates.length > 6 && (
                  <li className="italic">
                    + {readiness.applicable_templates.length - 6} more
                  </li>
                )}
              </ul>
            )}
            <p className="text-xs text-text-muted mt-3">
              Status will change from <em>Pending</em> to{" "}
              <em>In Progress</em>. You can&rsquo;t undo this — but
              assigned documents can be waived individually if something
              doesn&rsquo;t apply.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={closeOnboardModal}
                disabled={startingOnboarding}
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleConfirmOnboarding(false)}
                isLoading={startingOnboarding}
              >
                <Rocket className="h-4 w-4" />
                Start onboarding
              </Button>
            </div>
          </>
        )}
      </Modal>
    </motion.div>
  );
}

// ─────────────── Edit modal ───────────────
// Inline rather than a separate file — the form is small and the profile
// page is the only caller. Wires the "Edit" button in the header; without
// it the button had no onClick and silently did nothing.
function EditEmployeeModal({
  isOpen,
  onClose,
  employee,
  onSaved,
}: {
  isOpen: boolean;
  onClose: () => void;
  employee: Employee;
  onSaved: () => void;
}) {
  const { addToast } = useToast();
  const [firstName, setFirstName] = useState(employee.first_name);
  const [lastName, setLastName] = useState(employee.last_name);
  const [preferredName, setPreferredName] = useState(employee.preferred_name ?? "");
  const [pronouns, setPronouns] = useState(employee.pronouns ?? "");
  const [dateOfBirth, setDateOfBirth] = useState(employee.date_of_birth ?? "");
  const [sex, setSex] = useState(employee.sex ?? "");
  const [title, setTitle] = useState(employee.title);
  const [workEmail, setWorkEmail] = useState(employee.work_email ?? "");
  const [personalEmail, setPersonalEmail] = useState(employee.personal_email ?? "");
  const [phone, setPhone] = useState(employee.phone ?? "");
  const [smsOptIn, setSmsOptIn] = useState(
    Boolean(employee.sms_notifications_enabled),
  );
  const [workLocation, setWorkLocation] = useState(employee.work_location ?? "");
  const [officeLocation, setOfficeLocation] = useState(employee.office_location ?? "");
  const [saving, setSaving] = useState(false);

  // Re-seed fields whenever the modal opens for a (possibly newly loaded)
  // employee — otherwise stale values leak across edits.
  useEffect(() => {
    if (!isOpen) return;
    setFirstName(employee.first_name);
    setLastName(employee.last_name);
    setPreferredName(employee.preferred_name ?? "");
    setPronouns(employee.pronouns ?? "");
    setDateOfBirth(employee.date_of_birth ?? "");
    setSex(employee.sex ?? "");
    setTitle(employee.title);
    setWorkEmail(employee.work_email ?? "");
    setPersonalEmail(employee.personal_email ?? "");
    setPhone(employee.phone ?? "");
    setSmsOptIn(Boolean(employee.sms_notifications_enabled));
    setWorkLocation(employee.work_location ?? "");
    setOfficeLocation(employee.office_location ?? "");
  }, [isOpen, employee]);

  const handleSave = async () => {
    if (!firstName.trim() || !lastName.trim() || !title.trim()) {
      addToast("error", "First name, last name, and title are required");
      return;
    }
    // Build the payload separately from the call so we can log it on
    // failure. Every key we send must exist on EmployeeUpdate (Pydantic).
    // Extra keys produce a 422; type mismatches a different 422; RBAC
    // failures a 403; constraint hits a 500. We log enough on failure
    // that the next bug report can be diagnosed without a backend trace.
    const payload = {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      preferred_name: preferredName.trim() || null,
      pronouns: pronouns.trim() || null,
      date_of_birth: dateOfBirth || null,
      sex: sex || null,
      title: title.trim(),
      work_email: workEmail.trim() || null,
      personal_email: personalEmail.trim() || null,
      phone: phone.trim() || null,
      sms_notifications_enabled: smsOptIn,
      work_location: workLocation.trim() || null,
      office_location: officeLocation.trim() || null,
    };

    setSaving(true);
    try {
      await updateEmployee(employee.id, payload as Partial<Employee>);
      addToast("success", "Employee updated");
      onSaved();
    } catch (err) {
      const ax = err as {
        message?: string;
        code?: string;
        config?: { url?: string; method?: string };
        response?: {
          status?: number;
          statusText?: string;
          data?: unknown;
        };
      };

      // Loud diagnostic log — keep until the "Failed to update employee"
      // reports stop coming in. Includes the exact payload we sent, the
      // HTTP status, and the raw response body so a screenshot of the
      // console alone is enough to root-cause the next failure.
      console.error("[update-employee] FAILED", {
        employeeId: employee.id,
        payloadSent: payload,
        requestUrl: ax?.config?.url,
        requestMethod: ax?.config?.method,
        status: ax?.response?.status,
        statusText: ax?.response?.statusText,
        responseBody: ax?.response?.data,
        errorCode: ax?.code,
        errorMessage: ax?.message,
        raw: err,
      });

      const detail = (ax?.response?.data as { detail?: unknown })?.detail;
      let msg = "Failed to update employee";

      if (typeof detail === "string") {
        msg = detail;
      } else if (Array.isArray(detail)) {
        const first = detail[0] as { loc?: unknown[]; msg?: string } | undefined;
        if (first?.msg) {
          const field = String(first.loc?.[first.loc.length - 1] ?? "");
          msg = field ? `${field.replace(/_/g, " ")}: ${first.msg}` : first.msg;
        }
      } else if (detail && typeof detail === "object") {
        const d = detail as { message?: string; error?: string; detail?: string };
        msg = d.message || d.error || d.detail || JSON.stringify(detail);
      } else if (ax?.response?.status) {
        // Exposed status when the body had no usable detail — far better
        // than the bare "Failed to update employee" the user kept seeing.
        msg = `Failed to update employee (HTTP ${ax.response.status})`;
      } else if (ax?.code === "ERR_NETWORK" || ax?.message === "Network Error") {
        msg = "Network error — check your connection and try again.";
      }

      addToast("error", msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit employee" size="lg">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input
          label="First name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
        />
        <Input
          label="Last name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
        />
        <Input
          label="Preferred name"
          value={preferredName}
          onChange={(e) => setPreferredName(e.target.value)}
        />
        <Input
          label="Pronouns"
          value={pronouns}
          onChange={(e) => setPronouns(e.target.value)}
          placeholder="she/her"
        />
        <Input
          label="Date of birth"
          type="date"
          value={dateOfBirth}
          onChange={(e) => setDateOfBirth(e.target.value)}
        />
        <Select
          label="Sex (for benefits rating)"
          value={sex}
          onChange={(e) => setSex(e.target.value)}
          options={[
            { value: "", label: "—" },
            { value: "M", label: "Male" },
            { value: "F", label: "Female" },
          ]}
        />
        <Input
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="sm:col-span-2"
        />
        <Input
          label="Work email"
          type="email"
          value={workEmail}
          onChange={(e) => setWorkEmail(e.target.value)}
        />
        <Input
          label="Personal email"
          type="email"
          value={personalEmail}
          onChange={(e) => setPersonalEmail(e.target.value)}
        />
        <Input
          label="Phone"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
        <label className="flex items-start gap-2 text-sm text-text-primary sm:col-span-2 px-1 py-2">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={smsOptIn}
            onChange={(e) => setSmsOptIn(e.target.checked)}
            disabled={!phone.trim()}
          />
          <span>
            <span className="font-medium">Receive SMS notifications</span>
            <span className="block text-xs text-text-muted">
              Leave decisions, manager approvals, training reminders, and
              expense decisions ping the phone above. Requires Twilio to be
              configured at the platform level (Settings → Integrations).
            </span>
          </span>
        </label>
        <Input
          label="Work location"
          value={workLocation}
          onChange={(e) => setWorkLocation(e.target.value)}
          placeholder="Remote"
        />
        <Input
          label="Office location"
          value={officeLocation}
          onChange={(e) => setOfficeLocation(e.target.value)}
          className="sm:col-span-2"
        />
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} isLoading={saving}>
          Save changes
        </Button>
      </div>
    </Modal>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
      <dt className="text-sm text-text-muted">{label}</dt>
      <dd className="text-sm font-medium text-text-primary">{value}</dd>
    </div>
  );
}
